import { Box, Flex, SegmentedControl, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSliderInput } from 'components/common/form/slider';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { DotDotDot } from 'components/common/layout/dot-dot-dot';
import { VideoPlaceholder } from 'components/common/video/placeholder';
import useVideoLibrarySidebarStore from 'components/sections/video-library/sidebar/use-video-library-store';
import { MachineContext } from 'contexts/machine.context';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { IVerboseComponent } from 'interfaces/i-components';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HardwareVersion } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { clone } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { VideosService } from 'services/videos.service';
import { useVideosStore } from 'stores/use-videos-store';

const RENDER_SCALE = {
  /** how many decimals to round scaling steps to use and for display */
  ROUND_DECIMALS: 2,
  MIN: 0.8,
  MAX: 1.201,
  STEP: 0.05,
};

// * some weird stuff because floats are weird
const RENDER_SCALE_STEPS: number[] = (() => {
  const ROUND_FACTOR = Math.pow(10, RENDER_SCALE.ROUND_DECIMALS);

  const output: number[] = [];
  for (
    let i = RENDER_SCALE.MIN;
    i <= RENDER_SCALE.MAX;
    i = Math.round((i + RENDER_SCALE.STEP) * ROUND_FACTOR) / ROUND_FACTOR
  ) {
    output.push(i);
  }
  return output;
})();

enum TabKey {
  ReleaseFrame = 'videos.release-frame',
  MoundFrame = 'videos.mound-frame',
}

const getDictKey = (hw: HardwareVersion, tab: TabKey, scale: number) =>
  `${hw}_${tab}_${scale}`;

// used for both release and mound frame
export const VideoLibrarySidebarScalingTab = (props: IVerboseComponent) => {
  const model = useVideoLibrarySidebarStore((s) => s.model);

  const { machine } = useContext(MachineContext);
  const selectedVideo = useVideosStore((s) => s.selectedVideo);

  const [loading, setLoading] = useState(false);

  // key is [HardwareVersion]_[TabKey]_[scale]
  // value is base64 of image
  const [frameDict, setFrameDict] = useState<DictOf<string>>({});

  // clear loading flag only after frameDict updates
  useEffect(() => {
    setLoading(false);
  }, [frameDict]);

  const [hwVersion, setHwVersion] = useState(machine.hardware_version);

  const [scale, setScale] = useState(selectedVideo?.RenderScale ?? 1);

  const [activeTab, setActiveTab] = useState(TabKey.ReleaseFrame);

  // automatically update model RenderScale when scale changes
  useEffect(() => {
    if (!selectedVideo) {
      return;
    }

    if (!model) {
      return;
    }

    verboseDebug(props, `scale changed to ${scale}`);
    useVideoLibrarySidebarStore.getState().mergeModel({ RenderScale: scale });
  }, [scale]);

  // e.g. after frameDict is updated in fetchImages, auto-update image shown based on frameIndex
  const frameImage = useMemo(
    () => frameDict[getDictKey(hwVersion, activeTab, scale)],
    [frameDict, hwVersion, activeTab, scale]
  );

  const latestLoading = useLatest(loading);

  const fetchImages = useCallback(
    async (hw: HardwareVersion) => {
      if (!selectedVideo) {
        verboseDebug(props, 'fetch images > skipped > no selected video');
        return;
      }

      if (!model) {
        verboseDebug(props, 'fetch images > skipped > no model');
        return;
      }

      if (latestLoading.current) {
        verboseDebug(props, 'fetch images > skipped > loading');
        return;
      }

      const indices = [1, model.ReleaseFrame];

      if (indices.length === 0) {
        return;
      }

      verboseDebug(props, `fetch ${hw} images > indices`, indices);

      setLoading(true);

      const results = await VideosService.getInstance().extractFrames({
        mode: 'preview',
        video_id: selectedVideo._id,
        frameSpec: model,
        frame_indices: indices,
        scales: ArrayHelper.unique([scale, ...RENDER_SCALE_STEPS]),
        hardware_version: hw,
      });

      // * loading should revert to false after frameDict is updated
      setFrameDict((prev) => {
        const nextDict = clone(prev);

        results.forEach((frame) => {
          if (frame.scale === undefined) {
            // shouldn't trigger, but skip if it does
            return;
          }

          const tab =
            frame.frame === 1 ? TabKey.MoundFrame : TabKey.ReleaseFrame;
          nextDict[getDictKey(hw, tab, frame.scale)] = frame.base64;
        });

        return nextDict;
      });
    },
    [selectedVideo, model]
  );

  const latestFrameDict = useLatest(frameDict);

  useEffect(() => {
    if (
      Object.keys(latestFrameDict.current).findIndex((key) =>
        key.startsWith(hwVersion)
      ) !== -1
    ) {
      // already loaded previously
      return;
    }

    fetchImages(hwVersion);
  }, [hwVersion]);

  const hwOptions = useMemo(() => {
    return Object.values(HardwareVersion).map((t) => {
      const o: IOptionExt = {
        group: t === machine.hardware_version ? 'Current' : 'Other Versions',
        label: t,
        value: t,
      };

      return o;
    });
  }, [machine.machineID, machine.hardware_version]);

  if (!selectedVideo) {
    return null;
  }

  if (!model) {
    return null;
  }

  return (
    <ErrorBoundary identifier="VideoLibrarySidebarScalingTab">
      <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
        <CommonInputWrapper id="video-render-scale">
          <CommonInputLabel label="videos.render-scale" />

          <Text color={RADIX.COLOR.SECONDARY}>
            {t('videos.render-scale-msg')}
          </Text>

          <CommonSliderInput
            id="video-render-scale"
            step={RENDER_SCALE.STEP}
            min={RENDER_SCALE.MIN}
            max={RENDER_SCALE.MAX}
            value={scale}
            disabled={loading}
            onChange={(pos) => {
              const ROUND_FACTOR = Math.pow(10, RENDER_SCALE.ROUND_DECIMALS);
              const nextScale = Math.round(pos * ROUND_FACTOR) / ROUND_FACTOR;

              setScale(nextScale);
            }}
            hint_md={[
              `${t('common.current')}: ${scale.toFixed(
                RENDER_SCALE.ROUND_DECIMALS
              )}`,
              '|',
              `Min: ${RENDER_SCALE.MIN.toFixed(RENDER_SCALE.ROUND_DECIMALS)}x`,
              '-',
              `Max: ${RENDER_SCALE.MAX.toFixed(RENDER_SCALE.ROUND_DECIMALS)}x`,
            ].join(' ')}
          />
        </CommonInputWrapper>

        <CommonSelectInput
          id="video-hardware-version"
          name="hardware_version"
          label="common.hardware-version"
          disabled={loading}
          options={hwOptions}
          value={hwVersion}
          onChange={(v) => setHwVersion(v as HardwareVersion)}
        />

        <Box>
          <CommonInputWrapper id="preview-frames">
            <CommonInputLabel label="videos.preview-frames" />

            <SegmentedControl.Root
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as TabKey)}
              style={{ width: '100%' }}
            >
              {Object.values(TabKey).map((o, i) => (
                <SegmentedControl.Item key={`mode-${i}`} value={o}>
                  {t(o)}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl.Root>
          </CommonInputWrapper>
        </Box>

        <div
          style={{
            borderRadius: 'var(--radius-4)',
            width: '100%',
            height: 'auto',
          }}
        >
          {loading || !frameImage ? (
            <VideoPlaceholder>
              {loading ? (
                <DotDotDot label="common.loading" />
              ) : (
                t('videos.image-extraction-error-msg')
              )}
            </VideoPlaceholder>
          ) : (
            <img
              src={frameImage}
              style={{
                borderRadius: 'var(--radius-4)',
                width: '100%',
                height: 'auto',
              }}
            />
          )}
        </div>
      </Flex>
    </ErrorBoundary>
  );
};
